import {
  TrendingUp, Award, BarChart3, Wifi,
  PieChart as PieChartIcon,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const STATUS_LABELS = {
  registered: "Inscrit", checked_in: "Present", cancelled: "Annule", waitlisted: "En attente",
};
const CHART_COLORS = ["#0099CC", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#EC4899", "#06B6D4"];

export function StatsTab({ advancedStats, statsPeriod, setStatsPeriod }) {
  if (!advancedStats) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0099CC]" />
      </div>
    );
  }

  if (advancedStats.total === 0) {
    return (
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-12 text-center">
        <PieChartIcon className="h-12 w-12 text-[#2A3441] mx-auto mb-3" />
        <p className="text-[#64748b]">Aucune donnee statistique disponible</p>
        <p className="text-[#4A5568] text-sm mt-1">Les graphiques apparaitront des les premieres inscriptions.</p>
      </div>
    );
  }

  return (
    <>
      {/* Period filter */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="stats-period-filter">
        <span className="text-sm text-[#64748b]">Periode :</span>
        {[
          { value: "all", label: "Tout" },
          { value: "24h", label: "24h" },
          { value: "6h", label: "6h" },
          { value: "1h", label: "1h" },
        ].map(p => (
          <button key={p.value} data-testid={`period-${p.value}`}
            onClick={() => setStatsPeriod(p.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${statsPeriod === p.value ? "bg-[#0099CC] text-white" : "bg-[#1E293B] text-[#94A3B8] hover:bg-[#2A3441]"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="stats-kpi-grid">
        {[
          { value: advancedStats.total, label: "Inscrits", color: "#0099CC" },
          { value: advancedStats.checked_in, label: "Presents", color: "#10B981" },
          { value: advancedStats.absent, label: "Absents", color: "#F59E0B" },
          { value: advancedStats.cancelled, label: "Annulations", color: "#EF4444" },
          { value: advancedStats.waitlisted, label: "Liste d'attente", color: "#8B5CF6" },
        ].map((kpi, i) => (
          <div key={kpi.label} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4 text-center transition-all hover:border-[#2A3441] hover:-translate-y-0.5" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="text-2xl sm:text-3xl font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
            <div className="text-xs text-[#64748b] mt-1">{kpi.label}</div>
            {advancedStats.total > 0 && (
              <div className="mt-2 h-1 rounded-full bg-[#1E293B] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.round(kpi.value / advancedStats.total * 100)}%`, background: kpi.color }} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Attendance rate */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#10B981]" /> Taux de presence</h3>
          <span className="text-2xl font-bold text-[#10B981]">{advancedStats.attendance_rate?.toFixed(1)}%</span>
        </div>
        <div className="h-3 rounded-full bg-[#1E293B] overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#10B981] to-[#0099CC] transition-all duration-1000" style={{ width: `${advancedStats.attendance_rate || 0}%` }} />
        </div>
      </div>

      {/* Online/Physical presence */}
      {advancedStats.event_format && advancedStats.event_format !== "presentiel" && (
        <div className="bg-[#0B0F19] border border-[#1E3A5F] rounded-xl p-5" data-testid="online-presence-card">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Wifi className="h-4 w-4 text-[#3B82F6]" /> Presence physique vs en ligne</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-xl font-bold text-[#10B981]">{advancedStats.online_stats?.physical_present || 0}</div>
              <div className="text-xs text-[#64748b]">Presents physiquement</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#3B82F6]">{advancedStats.online_stats?.online_joined || 0}</div>
              <div className="text-xs text-[#64748b]">Connectes en ligne</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#F59E0B]">{advancedStats.online_stats?.online_duration_avg_seconds ? `${Math.round(advancedStats.online_stats.online_duration_avg_seconds / 60)}min` : "—"}</div>
              <div className="text-xs text-[#64748b]">Duree moy. connexion</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-[#8B5CF6]">{advancedStats.online_stats?.presence_validated || 0}</div>
              <div className="text-xs text-[#64748b]">Presence validee</div>
            </div>
          </div>
        </div>
      )}

      {/* Check-in timeline */}
      {advancedStats.timeline.length > 0 && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#10B981]" /> Arrivees par heure</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={advancedStats.timeline}>
              <defs>
                <linearGradient id="colorCheckin" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="hour" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#1E293B" }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#1E293B" }} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#151B2B", border: "1px solid #2A3441", borderRadius: 8, color: "#fff" }} labelStyle={{ color: "#94A3B8" }} />
              <Area type="monotone" dataKey="count" name="Check-ins" stroke="#10B981" fillOpacity={1} fill="url(#colorCheckin)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Category breakdown */}
        {advancedStats.categories.length > 0 && (
          <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><PieChartIcon className="h-4 w-4 text-[#0099CC]" /> Repartition par categorie</h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={advancedStats.categories} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3} dataKey="value" nameKey="name">
                  {advancedStats.categories.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: "#151B2B", border: "1px solid #2A3441", borderRadius: 8, color: "#fff" }} />
                <Legend formatter={v => <span style={{ color: "#94A3B8", fontSize: 12 }}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Status breakdown */}
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#8B5CF6]" /> Statut des participants</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={advancedStats.statuses.map(s => ({ ...s, label: STATUS_LABELS[s.name] || s.name }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#1E293B" }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#1E293B" }} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#151B2B", border: "1px solid #2A3441", borderRadius: 8, color: "#fff" }} />
              <Bar dataKey="value" name="Participants" radius={[6, 6, 0, 0]}>
                {advancedStats.statuses.map((s, i) => (
                  <Cell key={i} fill={s.name === "checked_in" ? "#10B981" : s.name === "registered" ? "#0099CC" : s.name === "cancelled" ? "#EF4444" : s.name === "waitlisted" ? "#8B5CF6" : "#64748b"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Registration timeline */}
      {advancedStats.registrations.length > 0 && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#F59E0B]" /> Evolution des inscriptions</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={advancedStats.registrations}>
              <defs>
                <linearGradient id="colorReg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#1E293B" }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} axisLine={{ stroke: "#1E293B" }} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: "#151B2B", border: "1px solid #2A3441", borderRadius: 8, color: "#fff" }} />
              <Area type="monotone" dataKey="cumul" name="Total inscrits" stroke="#F59E0B" fillOpacity={1} fill="url(#colorReg)" strokeWidth={2} />
              <Area type="monotone" dataKey="inscriptions" name="Nouvelles inscriptions" stroke="#0099CC" fill="transparent" strokeWidth={1.5} strokeDasharray="5 5" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* VIP breakdown */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-5">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Award className="h-4 w-4 text-[#F59E0B]" /> VIP vs Standard</h3>
        <div className="flex items-center gap-8 justify-center">
          {advancedStats.vip_breakdown.map((v, i) => (
            <div key={v.name} className="text-center">
              <div className={`text-3xl font-bold ${i === 0 ? "text-[#F59E0B]" : "text-[#94A3B8]"}`}>{v.value}</div>
              <div className="text-sm text-[#64748b] mt-1">{v.name}</div>
              <div className="text-xs text-[#4A5568] mt-0.5">
                {advancedStats.total > 0 ? `${Math.round(v.value / advancedStats.total * 100)}%` : "0%"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
